import { useQuery } from "@tanstack/react-query"
import { readContracts } from "@wagmi/core"
import { Address } from "viem"
import { useAccount, useConfig } from "wagmi"

import { selectIsConnected, useDappSelector } from "#/redux-state"
import {
  MEZO_CHAIN,
  ONE_MINUTE_IN_SECONDS,
  ONE_SECOND_IN_MILLISECONDS,
  queryKeysFactory,
} from "#/shared/constants"
import { Stake } from "#/shared/types"
import { goldskyApi } from "#/shared/utils"

import VeBtcAbi, {
  address as veBtcAddress,
} from "../../constants/artifacts/VeBTC"

const staleTime = ONE_MINUTE_IN_SECONDS * 5 * ONE_SECOND_IN_MILLISECONDS
const refetchInterval = staleTime

const useStakesFor = (staker: Address | undefined) => {
  const config = useConfig()

  return useQuery<Stake[]>({
    enabled: !!staker,
    queryKey: [...queryKeysFactory.userKeys.stakes(), staker],
    staleTime,
    refetchInterval,
    queryFn: async () => {
      if (!staker) return []

      const stakes = await goldskyApi.getStakesByStaker(staker)

      if (stakes.length === 0) return []

      // @ts-expect-error Figure out why types don't match
      const votingPowerData = await readContracts(config, {
        contracts: stakes.map((stake) => ({
          address: veBtcAddress,
          abi: VeBtcAbi,
          functionName: "balanceOfNFT",
          args: [stake.id],
          chainId: MEZO_CHAIN.id,
        })),
      })

      return stakes.map((stake, index) => {
        const unlockTimestamp = Number(stake.unlockAt)

        return {
          id: Number(stake.id).toString(),
          amount: BigInt(stake.amount),
          unlockTimestamp,
          createdAt: stake.initializedAt,
          lockDuration: Number(stake.lockDuration),
          isPermanent: stake.isPermanent,
          canBeUnlocked:
            !stake.isPermanent &&
            Math.floor(Date.now() / ONE_SECOND_IN_MILLISECONDS) >=
              unlockTimestamp,
          votingPower:
            votingPowerData[index].status === "success"
              ? votingPowerData[index].result
              : undefined,
        }
      })
    },
  })
}

const useStakes = () => {
  const { address } = useAccount()
  const isConnected = useDappSelector(selectIsConnected)

  return useStakesFor(isConnected ? address : undefined)
}

export default useStakes
